// Blocked and do-not-call list: labels, access, response readers and the call helpers the Calls ledger
// and Contacts use. Pure: no React, no CSS. Everything from the server is re-validated here; malformed
// entries are dropped and counted, and unknown lists, sources or events never reach the page as codes.
import type { Call } from '@bcio-voice/contracts';
import type { BlocklistCounts, BlocklistEntry, BlocklistHistoryEvent, BlocklistImportRecord, BlocklistInvalidRow, BlocklistList, BlocklistPerson, BlocklistSource } from '../../lib/api.ts';
import { hasPermission } from '../../lib/capabilities.ts';
import { formatAbsolute, formatPhone, parsePhoneInput } from '../../lib/format.ts';

export const BLOCK_LISTS: readonly BlocklistList[] = ['calls_inbound', 'calls_outbound', 'texts'];
export const BLOCK_SOURCES: readonly BlocklistSource[] = ['manual', 'csv', 'stop_reply', 'carrier_complaint'];

export const LIST_LABEL: Record<BlocklistList, string> = {
  calls_inbound: 'Incoming calls',
  calls_outbound: 'Outgoing calls',
  texts: 'Texts',
};

/** What each list does, said once beside its checkbox. */
export const LIST_HINT: Record<BlocklistList, string> = {
  calls_inbound: 'Calls from this number are refused before anyone’s phone rings.',
  calls_outbound: 'Nobody in the workspace can call this number. This is the do-not-call list.',
  texts: 'Texts to this number are refused and it’s recorded as opted out. Removing it later doesn’t opt it back in.',
};

export const SOURCE_LABEL: Record<BlocklistSource, string> = {
  manual: 'Added by hand',
  csv: 'CSV import',
  stop_reply: 'STOP reply',
  carrier_complaint: 'Carrier complaint',
};

/** Field limits from the contract. */
export const REASON_MAX = 200;
export const NOTE_MAX = 10_000;
export const IMPORT_MAX_ROWS = 5000;
export const PAGE_SIZE = 50;

export interface BlocklistAccess {
  read: boolean;
  manage: boolean;
}

/** `blocklist:manage` implies reading. The API adds both to owner and admin sessions, and read to agents and supervisors. */
export function blocklistAccess(permissions: readonly string[] | null | undefined): BlocklistAccess {
  const manage = hasPermission(permissions, 'blocklist:manage');
  return { read: manage || hasPermission(permissions, 'blocklist:read'), manage };
}

// ---- Readers ------------------------------------------------------------------------------------------

const E164 = /^\+[1-9][0-9]{7,14}$/;
const ID = /^[A-Za-z0-9_.:-]{1,128}$/;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** B6 answers bare bodies; older routes wrap them in `{data, meta}`. Returns the inner body either way. */
export function unwrap(body: unknown): unknown {
  if (isRecord(body) && 'data' in body && body.data !== undefined) {
    if (Array.isArray(body.data)) {
      const page = isRecord(body.page) ? body.page : isRecord(body.meta) ? body.meta : {};
      return { items: body.data, next_cursor: typeof page.next_cursor === 'string' ? page.next_cursor : null };
    }
    return body.data;
  }
  return body;
}

function text(value: unknown, max: number): string | null {
  if (typeof value !== 'string') return null;
  const t = value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, '');
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}
function timestamp(value: unknown): string | null {
  return typeof value === 'string' && !Number.isNaN(Date.parse(value)) ? value : null;
}
function person(value: unknown): BlocklistPerson | null {
  if (!isRecord(value)) return null;
  const name = text(value.name, 200)?.trim();
  return { id: typeof value.id === 'string' && ID.test(value.id) ? value.id : null, name: name || 'Someone in the workspace' };
}
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return typeof value === 'string' && (list as readonly string[]).includes(value) ? (value as T) : null;
}
function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

export function readEntry(value: unknown): BlocklistEntry | null {
  if (!isRecord(value)) return null;
  const id = typeof value.id === 'string' && ID.test(value.id) ? value.id : null;
  const e164 = typeof value.e164 === 'string' && E164.test(value.e164) ? value.e164 : null;
  const lists = Array.isArray(value.lists) ? BLOCK_LISTS.filter(l => (value.lists as unknown[]).includes(l)) : [];
  const reason = text(value.reason, REASON_MAX + 1);
  const addedAt = timestamp(value.added_at);
  if (!id || !e164 || lists.length === 0 || reason === null || !addedAt) return null;
  return {
    id,
    e164,
    lists,
    reason,
    note: text(value.note, NOTE_MAX + 1) ?? '',
    source: oneOf(BLOCK_SOURCES, value.source) ?? 'manual',
    source_ref: text(value.source_ref, 255),
    added_by: person(value.added_by) ?? { id: null, name: 'Someone in the workspace' },
    added_at: addedAt,
    expires_at: timestamp(value.expires_at),
    updated_at: timestamp(value.updated_at) ?? addedAt,
  };
}

export interface EntryPage {
  items: BlocklistEntry[];
  next: string | null;
  skipped: number;
}

/** Null when the body isn't a page at all. */
export function readPage(body: unknown): EntryPage | null {
  const v = unwrap(body);
  if (!isRecord(v) || !Array.isArray(v.items)) return null;
  const items: BlocklistEntry[] = [];
  let skipped = 0;
  for (const raw of v.items) {
    const entry = readEntry(raw);
    if (entry) items.push(entry);
    else skipped += 1;
  }
  return { items, next: typeof v.next_cursor === 'string' && v.next_cursor ? v.next_cursor : null, skipped };
}

export interface LookupResult {
  blocked: boolean;
  entry: BlocklistEntry | null;
}

export function readLookup(body: unknown): LookupResult | null {
  const v = unwrap(body);
  if (!isRecord(v) || typeof v.blocked !== 'boolean') return null;
  const entry = readEntry(v.entry);
  return { blocked: v.blocked && !!entry, entry };
}

export function readHistory(body: unknown): BlocklistHistoryEvent[] | null {
  const v = unwrap(body);
  if (!isRecord(v) || !Array.isArray(v.items)) return null;
  return v.items.flatMap(raw => {
    if (!isRecord(raw)) return [];
    const at = timestamp(raw.at);
    const event = typeof raw.event === 'string' && /^[a-z][a-z0-9_]{0,39}$/.test(raw.event) ? raw.event : null;
    return at && event ? [{ at, actor: person(raw.actor), event, detail: raw.detail ?? null }] : [];
  });
}

function readInvalid(value: unknown): BlocklistInvalidRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(raw => (isRecord(raw) && typeof raw.row === 'number' && Number.isInteger(raw.row) && raw.row >= 1
    ? [{ row: raw.row, e164: text(raw.e164, 100) ?? '', error: text(raw.error, 200) || 'This row can’t be imported.' }]
    : []));
}

export function readCounts(value: unknown): BlocklistCounts | null {
  if (!isRecord(value)) return null;
  return { added: count(value.added), updated: count(value.updated), unchanged: count(value.unchanged), invalid: readInvalid(value.invalid) };
}

export interface ImportResponse {
  kind: 'preview' | 'result';
  counts: BlocklistCounts;
  importId: string | null;
}

export function readImportResponse(body: unknown): ImportResponse | null {
  const v = unwrap(body);
  if (!isRecord(v)) return null;
  const result = readCounts(v.result);
  if (result) return { kind: 'result', counts: result, importId: typeof v.import_id === 'string' ? v.import_id : null };
  const preview = readCounts(v.preview);
  return preview ? { kind: 'preview', counts: preview, importId: null } : null;
}

export function readImports(body: unknown): BlocklistImportRecord[] | null {
  const v = unwrap(body);
  if (!isRecord(v) || !Array.isArray(v.items)) return null;
  return v.items.flatMap(raw => {
    if (!isRecord(raw)) return [];
    const at = timestamp(raw.at);
    const filename = text(raw.filename, 255);
    if (!at || !filename || typeof raw.id !== 'string') return [];
    const c = isRecord(raw.counts) ? raw.counts : {};
    const invalid = Array.isArray(c.invalid) ? c.invalid.length : count(c.invalid);
    return [{ id: raw.id, filename, by: person(raw.by), at, counts: { added: count(c.added), updated: count(c.updated), unchanged: count(c.unchanged), invalid } }];
  });
}

// ---- Words ---------------------------------------------------------------------------------------------

export function listsText(lists: readonly BlocklistList[]): string {
  const names = BLOCK_LISTS.filter(l => lists.includes(l)).map(l => LIST_LABEL[l].toLowerCase());
  if (names.length === 0) return 'no lists';
  return new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(names);
}

export function sourceText(entry: Pick<BlocklistEntry, 'source' | 'source_ref'>): string {
  const ref = sourceRef(entry);
  return ref ? `${SOURCE_LABEL[entry.source]}: ${ref}` : SOURCE_LABEL[entry.source];
}

/** The file name of a CSV import or a carrier's complaint reference; message ids of STOP replies aren't shown. */
export function sourceRef(entry: Pick<BlocklistEntry, 'source' | 'source_ref'>): string | null {
  return (entry.source === 'csv' || entry.source === 'carrier_complaint') && entry.source_ref ? entry.source_ref : null;
}

export function isExpired(entry: Pick<BlocklistEntry, 'expires_at'>, now = Date.now()): boolean {
  return !!entry.expires_at && Date.parse(entry.expires_at) <= now;
}

/**
 * An expiry is a day: the form sets the end of the chosen day in this browser's time zone, so it reads
 * back as that date, without a time and in the same zone it was picked in.
 */
export function expiryText(entry: Pick<BlocklistEntry, 'expires_at'>, now = Date.now()): string {
  if (!entry.expires_at) return 'Never';
  const date = new Date(entry.expires_at);
  if (Number.isNaN(date.getTime())) return 'Never';
  const when = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
  return isExpired(entry, now) ? `Expired ${when}` : `End of ${when}`;
}

const EVENT_LABEL: Record<string, string> = {
  added: 'Added to the list',
  updated: 'Changed',
  removed: 'Removed from the list',
  imported: 'Updated by a CSV import',
  expired: 'Expired',
  restored: 'Added back to the list',
};

export function historyLabel(event: string): string {
  return EVENT_LABEL[event] ?? event.charAt(0).toUpperCase() + event.slice(1).replace(/_/g, ' ');
}

/** Plain sentences for what an event changed, from the allowlisted fields of its detail only. */
export function historyDetail(event: BlocklistHistoryEvent): string[] {
  const d = isRecord(event.detail) ? event.detail : {};
  if (event.event === 'updated') {
    const before = readEntryLoose(d.before);
    const after = readEntryLoose(d.after);
    if (!before || !after) return [];
    const lines: string[] = [];
    if (before.lists.join() !== after.lists.join()) lines.push(`Lists changed from ${listsText(before.lists)} to ${listsText(after.lists)}.`);
    if (before.reason !== after.reason) lines.push(`Reason changed to “${after.reason}”.`);
    if (before.note !== after.note) lines.push(after.note ? 'Note changed.' : 'Note removed.');
    if (before.expires_at !== after.expires_at) lines.push(after.expires_at ? `Expiry set to ${formatAbsolute(after.expires_at)}.` : 'Expiry removed.');
    if (before.source !== after.source) lines.push(...originLine(after));
    return lines;
  }
  // Entries added by hand or import carry {entry}; a STOP reply mirrored from texting carries {after}.
  const entry = readEntryLoose(d.entry ?? d.after);
  if (event.event === 'added' && entry) return [`Lists: ${listsText(entry.lists)}.`, `Reason: “${entry.reason}”.`, ...originLine(entry)];
  if (event.event === 'removed') return d.texts_consent_unchanged === true ? ['The texts opt-out stays in place.'] : [];
  return [];
}

/** Where an entry's lists came from, when that's more than "added by hand". */
function originLine(entry: { source: BlocklistSource; source_ref: string | null }): string[] {
  if (entry.source === 'csv') return [entry.source_ref ? `From the CSV file ${entry.source_ref}.` : 'From a CSV import.'];
  if (entry.source === 'stop_reply') return ['The person replied STOP to a text.'];
  if (entry.source === 'carrier_complaint') return [entry.source_ref ? `From carrier complaint ${entry.source_ref}.` : 'From a carrier complaint.'];
  return [];
}

/** History snapshots keep the entry's fields; only the ones shown are read. */
function readEntryLoose(value: unknown): { lists: BlocklistList[]; reason: string; note: string; expires_at: string | null; source: BlocklistSource; source_ref: string | null } | null {
  if (!isRecord(value) || !Array.isArray(value.lists)) return null;
  return {
    lists: BLOCK_LISTS.filter(l => (value.lists as unknown[]).includes(l)),
    reason: text(value.reason, REASON_MAX + 1) ?? '',
    note: text(value.note, NOTE_MAX + 1) ?? '',
    expires_at: timestamp(value.expires_at),
    source: oneOf(BLOCK_SOURCES, value.source) ?? 'manual',
    source_ref: text(value.source_ref, 255),
  };
}

/**
 * The search box takes a number in any format, part of one, or words from the reason or note. The API
 * matches a substring of "e164 reason note", so whole numbers go as E.164 and partial ones as digits.
 */
export function searchParam(input: string): string {
  const trimmed = input.trim().slice(0, 200);
  const phone = parsePhoneInput(trimmed);
  if (phone.ok) return phone.e164;
  if (/^[\d\s().+-]+$/.test(trimmed)) return trimmed.replace(/[^\d+]/g, '');
  return trimmed;
}

// ---- Calls ---------------------------------------------------------------------------------------------

export interface CallBlock {
  list: BlocklistList | null;
  reason: string | null;
  entryId: string | null;
}

/** The refusal on a call record (`outcome: blocked`, `blocked {list, reason, entry_id}`), or null. */
export function callBlock(call: Call): CallBlock | null {
  const raw = (call as { blocked?: unknown }).blocked;
  const outcome = (call.outcome as string | null) === 'blocked';
  if (!isRecord(raw)) return outcome ? { list: null, reason: null, entryId: null } : null;
  return {
    list: oneOf(BLOCK_LISTS, raw.list),
    reason: text(raw.reason, REASON_MAX + 1)?.trim() || null,
    entryId: typeof raw.entry_id === 'string' && ID.test(raw.entry_id) ? raw.entry_id : null,
  };
}

/** The outside number on a call, the one a block applies to. Null for internal calls and withheld numbers. */
export function callNumber(call: Call): string | null {
  if (call.direction === 'inbound') return call.from.e164 && E164.test(call.from.e164) ? call.from.e164 : null;
  if (call.destination.kind === 'pstn' && E164.test(call.destination.e164)) return call.destination.e164;
  return null;
}

/** One sentence for a blocked call: which list refused it and why. */
export function blockSentence(call: Call, block: CallBlock): string {
  const number = callNumber(call);
  const who = number ? formatPhone(number) : 'This number';
  const list = block.list === 'calls_inbound' ? 'blocked for incoming calls' : block.list === 'calls_outbound' ? 'on the do-not-call list' : block.list === 'texts' ? 'blocked for texts' : 'on your workspace’s blocked list';
  const refused = call.direction === 'inbound' ? 'Sidetone refused this call' : 'Sidetone stopped this call before it was placed';
  return `${refused}. ${who} is ${list}.`;
}

/** Compact ledger text for the reason, clipped at a word where it can be; the full reason goes in the title. */
export function clipReason(reason: string, max = 48): string {
  if (reason.length <= max) return reason;
  const cut = reason.slice(0, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}
